import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Moon, Sun, Clock, Star, TrendingUp, Calendar, Sparkles,
  Save, BedDouble, Sunrise, BarChart3, ArrowLeft
} from 'lucide-react';

const API = '/api';

const qualityOptions = [
  { value: 1, label: 'Çok Kötü', emoji: '😫', color: 'bg-red-100 border-red-300 text-red-700' },
  { value: 2, label: 'Kötü', emoji: '😕', color: 'bg-orange-100 border-orange-300 text-orange-700' },
  { value: 3, label: 'Normal', emoji: '😐', color: 'bg-yellow-100 border-yellow-300 text-yellow-700' },
  { value: 4, label: 'İyi', emoji: '😊', color: 'bg-green-100 border-green-300 text-green-700' },
  { value: 5, label: 'Mükemmel', emoji: '😴', color: 'bg-emerald-100 border-emerald-300 text-emerald-700' }
];

export default function SleepTracker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todaySleep, setTodaySleep] = useState(null);
  const [history, setHistory] = useState([]);

  // Form state
  const [duration, setDuration] = useState('');
  const [quality, setQuality] = useState(null);
  const [bedtime, setBedtime] = useState('');
  const [waketime, setWaketime] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [todayRes, historyRes] = await Promise.all([
        axios.get(`${API}/sleep/today`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sleep/history?days=7`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (todayRes.data.has_sleep) {
        setTodaySleep(todayRes.data.sleep);
        const s = todayRes.data.sleep;
        setDuration(s.duration_hours?.toString() || '');
        setQuality(s.quality);
        setBedtime(s.bedtime || '');
        setWaketime(s.waketime || '');
        setNotes(s.notes || '');
      }

      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!duration || !quality) {
      toast.error('Uyku süresi ve kalitesi gerekli');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/sleep/log`,
        {
          duration_hours: parseFloat(duration),
          quality,
          bedtime: bedtime || null,
          waketime: waketime || null,
          notes: notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTodaySleep(response.data.sleep);
      toast.success('Uyku kaydedildi! 🌙');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const getQualityColor = (q) => {
    const opt = qualityOptions.find(o => o.value === q);
    return opt?.color || 'bg-gray-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <section className="py-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Moon className="h-10 w-10" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">Uyku Takibi</h1>
          <p className="text-center text-white/90">Kaliteli uyku, sağlıklı yaşamın temelidir</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Tabs defaultValue="today" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="today">Bugün</TabsTrigger>
            <TabsTrigger value="history">Haftalık</TabsTrigger>
          </TabsList>

          {/* Today Tab */}
          <TabsContent value="today" className="space-y-6">
            {/* AI Insight */}
            {todaySleep?.ai_insight && (
              <Card className="border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-yellow-200 p-3 rounded-full">
                      <Sparkles className="h-6 w-6 text-yellow-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-yellow-800 mb-1">AI Uyku Yorumu</h3>
                      <p className="text-yellow-900">{todaySleep.ai_insight}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-indigo-600" />
                  <div className="text-3xl font-bold">{duration || '-'}</div>
                  <div className="text-sm text-gray-500">saat uyku</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <Star className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                  <div className="text-3xl font-bold">
                    {quality ? qualityOptions.find(o => o.value === quality)?.emoji : '-'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {quality ? qualityOptions.find(o => o.value === quality)?.label : 'Kalite'}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Input Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BedDouble className="h-5 w-5 mr-2 text-indigo-600" />
                  Uyku Kaydı
                </CardTitle>
                <CardDescription>Dün geceki uykunuzu kaydedin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Duration */}
                <div>
                  <Label>Uyku Süresi (saat)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="24"
                    placeholder="7.5"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="text-lg"
                  />
                </div>

                {/* Quality */}
                <div>
                  <Label className="mb-3 block">Uyku Kalitesi</Label>
                  <div className="grid grid-cols-5 gap-2">
                    {qualityOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setQuality(opt.value)}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1
                          ${quality === opt.value ? `${opt.color} scale-105 ring-2 ring-offset-1` : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                      >
                        <span className="text-2xl">{opt.emoji}</span>
                        <span className="text-xs font-medium">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Times */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center">
                      <Moon className="h-4 w-4 mr-1" /> Yatış Saati
                    </Label>
                    <Input
                      type="time"
                      value={bedtime}
                      onChange={(e) => setBedtime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center">
                      <Sunrise className="h-4 w-4 mr-1" /> Kalkış Saati
                    </Label>
                    <Input
                      type="time"
                      value={waketime}
                      onChange={(e) => setWaketime(e.target.value)}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Not (opsiyonel)</Label>
                  <Textarea
                    placeholder="Rüya gördüm, gece uyandım..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />
                </div>

                <Button onClick={handleSave} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  {saving ? 'Kaydediliyor...' : <><Save className="h-4 w-4 mr-2" /> Kaydet</>}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            {/* Weekly Summary */}
            {history.summary && (
              <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-indigo-600" />
                    Haftalık Özet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-indigo-600">{history.summary.avg_duration_hours}</div>
                      <div className="text-sm text-gray-500">Ort. Süre (saat)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-600">{history.summary.avg_quality}/5</div>
                      <div className="text-sm text-gray-500">Ort. Kalite</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{history.summary.days_logged}</div>
                      <div className="text-sm text-gray-500">Gün Kayıt</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trend Chart */}
            {history.trend && history.trend.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                    Uyku Trendi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between h-32 gap-2">
                    {history.trend.map((day, idx) => {
                      const maxDuration = Math.max(...history.trend.map(d => d.duration || 0)) || 1;
                      const height = ((day.duration || 0) / maxDuration) * 100;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center">
                          <div className="text-xs text-gray-500 mb-1">{day.quality}/5</div>
                          <div
                            className="w-full bg-indigo-500 rounded-t transition-all"
                            style={{ height: `${Math.max(height, 5)}%` }}
                          />
                          <span className="text-xs text-gray-500 mt-1">
                            {new Date(day.date).toLocaleDateString('tr-TR', { weekday: 'short' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sleep List */}
            <Card>
              <CardHeader>
                <CardTitle>Son Uyku Kayıtları</CardTitle>
              </CardHeader>
              <CardContent>
                {history.logs && history.logs.length > 0 ? (
                  <div className="space-y-3">
                    {history.logs.map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium">
                            {new Date(log.date).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'short' })}
                          </div>
                          <div className="text-sm text-gray-500">
                            {log.duration_hours} saat
                            {log.bedtime && log.waketime && ` • ${log.bedtime} - ${log.waketime}`}
                          </div>
                        </div>
                        <Badge className={getQualityColor(log.quality)}>
                          {qualityOptions.find(o => o.value === log.quality)?.emoji} {log.quality}/5
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Henüz uyku kaydı yok</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
